import React, { useState } from "react";
import CustomButton from "components/common/Button/CustomButton";
import { USER_ROLE } from "Constants/constant";
import { roleAccess } from "helpers/RolesPermission";
import { BiPencil } from "react-icons/bi";
import { FaClone } from "react-icons/fa";
import { MdPublish } from "react-icons/md";
import { connect } from "react-redux";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import api from "store/services/superAdmin.services";
import silApi from "store/services/sil.services";
import { updateSilFormTemplatesLocally } from "store/actions/sil.actions";
import SweetAlertConfirmationModal from "components/common/SweetAlertConfirmation/SweetAlertConfirmation";
import { HiEye, HiEyeOff, HiTrash } from "react-icons/hi";
import { apiDeleteRequest, apiPatchRequest } from "helpers/Requests";
import { navigateToClonedFormDraft } from "components/DynamicForms/shared/formTemplateClone";
import FormTemplateCloneModal from "components/DynamicForms/shared/FormTemplateCloneModal";
import { getUrlRole } from "Utils/utils";
import { resolveFormTemplateRowStatus } from "components/DynamicForms/shared/formCategoryUtils";

const SilFormListActionButtons = (props) => {
  const {
    formTemplateStatus,
    template,
    editFormTemplateHandler,
    updateFormTemplateLocally,
    routeRole,
    isUserGracePeriodOver,
    handleFetch,
    formCategoriesList,
  } = props;

  const navigate = useNavigate();

  const [selectedPublishTemp, setSelectedPublishTemp] = useState(null);
  const [showConfirmationModalPublish, setShowConfirmationModalPublish] =
    useState(false);
  const [showConfirmationModalDelete, setShowConfirmationModalDelete] =
    useState(false);

  const templateStatus = resolveFormTemplateRowStatus(
    template,
    formTemplateStatus?.value
  );

  const refreshList = () => {
    if (!handleFetch) return;

    const payload = {
      status: formTemplateStatus?.value ?? "all",
      page: 1,
    };

    handleFetch(payload, null);
  };

  const viewFormTemplateHandler = () => {
    const viewStatus =
      templateStatus === "hidden" ? "hidden" : templateStatus === "draft" ? "draft" : "publish";

    if (roleAccess([USER_ROLE.Super_Admin])) {
      navigate(`/${routeRole}/view-template/${template.uuid}/${viewStatus}`);
    } else if (roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])) {
      navigate(
        `/view-template/${routeRole}/${template.slug}/${viewStatus}/read-only`
      );
    }
  };

  const handleHideTemplate = async () => {
    try {
      const url = `/${routeRole}/form/template/hide/${template.uuid}`;
      await apiPatchRequest(url, { is_hidden: true });
      toast.success("Form has been hidden successfully!");
      updateFormTemplateLocally({ id: template.uuid, type: "hide" });
      refreshList();
    } catch (err) {
      toast.error("Something went wrong while hiding form!");
      console.log("err", err);
    }
  };

  const handleUnhideTemplate = async () => {
    try {
      let url = `/${routeRole}/form/template/unhide/${template.uuid}`;
      try {
        await apiPatchRequest(url);
      } catch (err) {
        url = `/${getUrlRole()}/${routeRole}/form-templates/unhide`;
        await silApi.post(url, { uuid: template.uuid });
      }
      toast.success("Form has been unhidden successfully!");
      updateFormTemplateLocally({ id: template.uuid, type: "unhide" });
      refreshList();
    } catch (err) {
      toast.error("Something went wrong while unhiding form!");
      console.log("err", err);
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      const url = `/${routeRole}/form/template/delete/${template.uuid}`;
      await apiDeleteRequest(url);
      toast.success("Form Template Deleted Successfully!");
      updateFormTemplateLocally({ id: template.uuid, type: "delete" });
      refreshList();
    } catch (err) {
      toast.error("Something went wrong while deleting!");
      console.log("err", err);
    }
  };

  const handlePublishDraftTemplate = async (templateId) => {
    try {
      if (isUserGracePeriodOver) return;
      setSelectedPublishTemp(templateId);

      const response = roleAccess([USER_ROLE.Super_Admin])
        ? await api.silFormDraftToPublishApi({
            id: templateId,
            role: routeRole,
          })
        : await silApi.silFormDraftToPublishApi({
            id: templateId,
            role: routeRole,
          });

      if (response) {
        toast.success("Form Template Published Successfully!");
      }

      updateFormTemplateLocally({ id: templateId, type: "delete" });
      setSelectedPublishTemp(null);
      refreshList();
    } catch (err) {
      setSelectedPublishTemp(null);
      toast.error(err.message ?? "Something went wrong!");
      console.log("err", err);
    }
  };

  const navigateToDraftEdit = () => {
    const encode = window.btoa(encodeURIComponent("draft"));
    const identifier = roleAccess([USER_ROLE.Super_Admin])
      ? template.uuid
      : template.slug;
    navigate(`/sil/edit-template/${identifier}/${encode}`);
  };

  const [showCloneModal, setShowCloneModal] = useState(false);

  const handleCloneCompleted = ({ clonedForm, sourceTitle }) => {
    navigateToClonedFormDraft(navigate, {
      role: "sil",
      routeRole: "sil",
      clonedForm,
      sourceTitle,
    });
    refreshList();
  };

  return (
    <>
      <div className='form-admin-actions'>
        {roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]) &&
          (templateStatus === "draft" ||
            (templateStatus === "publish" && !template.is_hidden)) && (
            <>
              {templateStatus === "publish" ? (
                <>
                  <button
                    type='button'
                    className='bg-white hover:bg-green-500 hover:text-white text-green-600 rounded-md'
                    onClick={() => editFormTemplateHandler(template)}
                  >
                    <div className='icon-pencil-wrapper' title='Edit'>
                      <BiPencil className='icon-size' />
                    </div>
                  </button>
                  <button
                    type='button'
                    className='bg-white rounded-md'
                    onClick={() => setShowCloneModal(true)}
                  >
                    <div className='icon-clone-wrapper' title='Clone'>
                      <FaClone className='icon-size' />
                    </div>
                  </button>
                </>
              ) : (
                <>
                  <CustomButton
                    isDisabled={
                      template.id === selectedPublishTemp ||
                      isUserGracePeriodOver
                    }
                    showLoading={template.id === selectedPublishTemp}
                    clickHandler={() => {
                      if (isUserGracePeriodOver) return;
                      setShowConfirmationModalPublish(true);
                    }}
                    label='Publish'
                    Icon={<MdPublish />}
                  />
                  <button
                    type='button'
                    disabled={template.id === selectedPublishTemp}
                    className='bg-white hover:bg-green-500 hover:text-white text-green-600 rounded-md'
                    onClick={navigateToDraftEdit}
                  >
                    <div className='icon-pencil-wrapper' title='Edit'>
                      <BiPencil className='icon-size' />
                    </div>
                  </button>
                </>
              )}
            </>
          )}

        {roleAccess([USER_ROLE.Super_Admin]) &&
          (templateStatus === "draft" ||
            (templateStatus === "publish" && !template.is_hidden)) &&
          (templateStatus === "publish" ? (
            <button
              type='button'
              onClick={() => setShowCloneModal(true)}
            >
              <div className='icon-clone-wrapper' title='Clone'>
                <FaClone className='icon-size' />
              </div>
            </button>
          ) : templateStatus === "draft" ? (
            <>
              <CustomButton
                isDisabled={template.id === selectedPublishTemp}
                showLoading={template.id === selectedPublishTemp}
                clickHandler={() => setShowConfirmationModalPublish(true)}
                label='Publish'
                Icon={<MdPublish />}
              />
              <button
                type='button'
                disabled={template.id === selectedPublishTemp}
                className='bg-white hover:bg-green-500 hover:text-white text-green-600 rounded-md'
                onClick={navigateToDraftEdit}
              >
                <div className='icon-pencil-wrapper' title='Edit'>
                  <BiPencil className='icon-size' />
                </div>
              </button>
            </>
          ) : null)}

        {roleAccess([
          USER_ROLE.Sp_Admin,
          USER_ROLE.Super_Admin,
          USER_ROLE.Sp_manager,
        ]) && (
          <>
            <button type='button' onClick={viewFormTemplateHandler}>
              <div className='icon-eye-wrapper' title='View'>
                <HiEye className='icon-size' />
              </div>
            </button>

            {(templateStatus === "publish" || templateStatus === "hidden") && (
              <>
                {template.is_hidden ? (
                  <button type='button' onClick={handleUnhideTemplate}>
                    <div className='icon-hide-wrapper' title='Unhide'>
                      <HiEye className='icon-size' />
                    </div>
                  </button>
                ) : (
                  templateStatus === "publish" && (
                    <button type='button' onClick={handleHideTemplate}>
                      <div className='icon-hide-wrapper' title='Hide'>
                        <HiEyeOff className='icon-size' />
                      </div>
                    </button>
                  )
                )}
              </>
            )}

            <button
              type='button'
              onClick={() => setShowConfirmationModalDelete(true)}
            >
              <div className='icon-delete-wrapper' title='Delete'>
                <HiTrash className='icon-size' />
              </div>
            </button>
          </>
        )}
      </div>

      {showConfirmationModalPublish && (
        <SweetAlertConfirmationModal
          isOpen={showConfirmationModalPublish}
          title='Confirm your action?'
          content='Are you sure to publish this form?'
          setIsConfirm={(e) => {
            setShowConfirmationModalPublish(false);
            if (e) handlePublishDraftTemplate(template.id);
          }}
        />
      )}

      {showConfirmationModalDelete && (
        <SweetAlertConfirmationModal
          isOpen={showConfirmationModalDelete}
          title='Confirm your action?'
          content='Are you sure you want to delete this form? This action cannot be undone.'
          setIsConfirm={(e) => {
            setShowConfirmationModalDelete(false);
            if (e) handleDeleteTemplate(template.uuid);
          }}
        />
      )}
      <FormTemplateCloneModal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        template={template}
        role='sil'
        routeRole='sil'
        onCloned={handleCloneCompleted}
        prefetchedCategories={formCategoriesList}
      />
    </>
  );
};

const mapDispatchToProps = {
  updateFormTemplateLocally: updateSilFormTemplatesLocally,
};

export default connect(null, mapDispatchToProps)(SilFormListActionButtons);
